import ContactSupportIcon from "@shared/svg/contact-support.svg?react";
import { AlertBox } from "@shared/ui/AlertBox";
import Headline, { HEADLINE_SIZES } from "@shared/ui/Headline";
import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "~/components/general";
import { canOpenPath, landingPath } from "~/constants/routePermissions";
import type enCommon from "~/lang/en-US/common.json";
import type { UserType } from "~/types/user";

/**
 * Set while "Go back" steps back from a blocked page. If the page it lands on
 * is blocked too (the role lost a whole module, so its list is gone as well),
 * the user goes straight to a module they can open instead of seeing the
 * modal again for every page of that module in their history.
 */
let steppingBackFromBlockedPage = false;

/**
 * Stops a user from opening a module their role doesn't include. The page
 * isn't rendered at all (its queries would only be refused); the no-access
 * modal shows instead. "Go back" steps back in the browser history, or goes
 * to a module they can open when there's nothing allowed to go back to.
 */
const ModuleAccess = ({ children }: { children: React.ReactNode }) => {
  const { user } = (useAuth() || {}) as { user?: UserType };
  const { messages } = useIntl() as unknown as {
    messages: { common?: Partial<typeof enCommon> };
  };
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const allowed = canOpenPath(user, pathname);
  const leaveDirectly = !allowed && steppingBackFromBlockedPage;

  useEffect(() => {
    if (allowed) {
      steppingBackFromBlockedPage = false;
    } else if (leaveDirectly) {
      steppingBackFromBlockedPage = false;
      navigate(landingPath(user), { replace: true });
    }
  }, [allowed, leaveDirectly, navigate, user]);

  if (allowed) return <>{children}</>;
  if (leaveDirectly) return null;

  const msg = messages?.common?.noAccess;

  // Same look as the Industrial AI access modal (FeatureAccess).
  return (
    <AlertBox
      isOpen
      overlay
      image={<ContactSupportIcon width="130" height="130" />}
      alertContent={
        <Headline size={HEADLINE_SIZES.X_SMALL}>{msg?.description}</Headline>
      }
      acceptButtonText={msg?.goBack}
      onAccept={() => {
        // React Router numbers its history entries; 0 is the first page the
        // app opened on, so there's nothing in the app to go back to.
        if ((window.history.state?.idx ?? 0) > 0) {
          steppingBackFromBlockedPage = true;
          navigate(-1);
        } else {
          navigate(landingPath(user), { replace: true });
        }
      }}
    />
  );
};

export default ModuleAccess;
